import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";

/** How long the copy button stays checked after a copy. */
const COPY_CONFIRMATION_MS = 4000;

export interface AgentPhoneNumberSectionProps {
  phoneNumber: string | null;
}

/**
 * The agent's assigned number, read-only. Assigning it is an HQ operation; all
 * this surface does is make clear the same number is both the outbound caller
 * ID and the number customers reach the agent on.
 */
export const AgentPhoneNumberSection: FC<AgentPhoneNumberSectionProps> = (
  props,
) => {
  const { phoneNumber } = props;

  const [isCopied, setIsCopied] = useState(false);

  // Companies without outbound calling only ever receive on this number, so
  // describing it as a caller ID would advertise something they don't have.
  const isOutboundCallingAvailable = useIsFeatureFlagEnabled(
    FeatureFlagKey.OutboundVoiceAgents,
  );

  const handleCopy = () => {
    if (!phoneNumber) {
      return;
    }

    setIsCopied(true);
    copy(phoneNumber);
    setTimeout(() => setIsCopied(false), COPY_CONFIRMATION_MS);
    showToast({
      title: "Phone number copied to clipboard",
      variant: ToastVariant.Success,
    });
  };

  return (
    <section aria-labelledby="phone-number-heading">
      <h2
        id="phone-number-heading"
        className="text-base font-semibold text-foreground"
      >
        Phone number
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {isOutboundCallingAvailable
          ? "This number is used for outbound caller ID and receives incoming calls."
          : "Calls to this number are handled by this agent."}
      </p>

      <div className="mt-3 flex h-7 items-center gap-1">
        <span
          className={twMerge(
            "text-sm font-medium",
            phoneNumber ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {phoneNumber ?? "Phone number not configured"}
        </span>
        {phoneNumber ? (
          <IconButton
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            icon={
              isCopied ? (
                <IconCheck className="size-4 text-blue-600" />
              ) : (
                <IconCopy className="size-4 text-muted-foreground" />
              )
            }
            onClick={handleCopy}
            accessibleLabel={
              isCopied ? "Phone number copied" : "Copy phone number"
            }
          />
        ) : null}
      </div>
    </section>
  );
};
